import { useTranslation } from 'react-i18next';

import {
  TGetDeviceConfigProps,
  TGetFeatureProps,
  TGetFileTypeProps,
  TGetIdeProps,
  TGetVendorProps,
} from '@/shared/hooks/use-vendor/use-vendor.types';
import { useReadTenantQuery } from '@/shared/redux/api/ident/ident-api';
import {
  DeviceTypeEnum,
  FeatureConfigFragment,
  FileTypeFragment,
  ProjectTypeEnum,
} from '@/shared/types/types.generated';

export const useVendor = () => {
  const { data, isLoading } = useReadTenantQuery();
  const vendors = data?.readTenant.vendors || [];

  const getVendor = ({ vendorId }: Partial<TGetVendorProps> = {}) => {
    return vendors?.find((vendor) => vendor.vendorId === vendorId);
  };

  const getIde = ({ vendorId, ideConfigId }: Partial<TGetIdeProps> = {}) => {
    const vendor = getVendor({ vendorId });
    return vendor?.ides?.find((ide) => ide.ideConfigId === ideConfigId);
  };

  const getProjectTypes = (props: Partial<TGetIdeProps> = {}) => {
    const ide = getIde(props);

    if (!ide?.fileTypes?.length) return [];

    const uniqueProjectTypes = new Set(
      ide.fileTypes.map((fileType) => fileType.projectType),
    );

    return Array.from(uniqueProjectTypes);
  };

  const getDeviceTypes = (props: Partial<TGetIdeProps> = {}) => {
    const ide = getIde(props);
    if (!ide?.devices?.length) return [];

    const uniqueDeviceTypes = new Set(
      ide.devices.map((device) => device.deviceType),
    );

    return Array.from(uniqueDeviceTypes);
  };

  const getDeviceConfig = ({
    vendorId,
    ideConfigId,
    deviceType,
  }: Partial<TGetDeviceConfigProps> = {}) => {
    const ide = getIde({ vendorId, ideConfigId });
    return ide?.devices?.find((device) => device.deviceType === deviceType);
  };

  const getExtensionsForIde = (
    vendorId: string,
    ideConfigId: string,
    projectType?: ProjectTypeEnum,
  ) => {
    const ide = getIde({ vendorId, ideConfigId });

    if (!ide?.fileTypes?.length) {
      return [];
    }

    return ide.fileTypes
      .filter(
        (fileType) => !projectType || fileType.projectType === projectType,
      )
      .map((fileType) => fileType.extension);
  };

  const getFeature = ({
    vendorId,
    ideConfigId,
    feature,
    ...props
  }: Partial<TGetFeatureProps> = {}): FeatureConfigFragment | undefined => {
    const ide = getIde({ vendorId, ideConfigId });

    const features =
      ide?.features?.filter(
        ({ feature: featureId }) => featureId === feature,
      ) ?? [];

    if (props.deviceType) {
      return features.find((feature) =>
        getDeviceTypesForFeature(feature)?.includes(props.deviceType!),
      );
    }

    if (props.projectType) {
      return features.find((feature) =>
        getProjectTypesForFeature(feature)?.includes(props.projectType!),
      );
    }

    return features?.[0];
  };

  const getDeviceTypesForFeature = (
    feature?: FeatureConfigFragment,
  ): DeviceTypeEnum[] => {
    const deviceTypes: DeviceTypeEnum[] = [];

    feature?.metaData?.deviceTypes?.forEach((deviceType) =>
      deviceTypes.push(deviceType),
    );
    feature?.metaData?.fileExtensionsToDeviceTypes
      ?.map(({ deviceType }) => deviceType)
      ?.forEach((deviceType) => deviceTypes.push(deviceType));

    return [...new Set(deviceTypes)];
  };

  const getProjectTypesForFeature = (
    feature?: FeatureConfigFragment,
  ): ProjectTypeEnum[] => {
    const projectTypes: ProjectTypeEnum[] = [];

    feature?.metaData?.fileExtensionsToDeviceTypes?.forEach(({ projectType }) =>
      projectTypes.push(projectType),
    );
    feature?.metaData?.fileToFileExtensions?.forEach(({ projectType }) =>
      projectTypes.push(projectType),
    );
    feature?.metaData?.projectFileExtensions?.forEach(({ projectType }) =>
      projectTypes.push(projectType),
    );

    return [...new Set(projectTypes)];
  };

  const isFeatureSupported = (
    props: Partial<TGetFeatureProps> = {},
  ): boolean => {
    return !!getFeature(props);
  };

  const getFileType = ({
    vendorId,
    ideConfigId,
    projectType,
    extension,
  }: TGetFileTypeProps = {}): FileTypeFragment | undefined => {
    if (!projectType || !vendorId || !ideConfigId) return undefined;

    const ide = getIde({ vendorId, ideConfigId });
    const fileTypes =
      ide?.fileTypes?.filter(
        (fileType) => fileType.projectType === projectType,
      ) ?? [];

    if (fileTypes.length <= 1) {
      return fileTypes?.[0];
    }

    // Several extensions share this project type; disambiguate by the
    // selected file extension when available.
    const byExtension =
      extension &&
      fileTypes.find((fileType) => fileType.extension === extension);

    if (byExtension) {
      return byExtension;
    }

    // No extension match yet; fall back to the first that defines inputs.
    return (
      fileTypes?.find((fileType) => fileType.additionalInputs?.length) ??
      fileTypes?.[0]
    );
  };

  const getFeatureType = (props: Partial<TGetFeatureProps> = {}) => {
    return getFeature(props)?.metaData.featureType;
  };

  return {
    isLoading,
    vendors,
    getVendor,
    getIde,
    getProjectTypes,
    getExtensionsForIde,
    getDeviceTypes,
    getDeviceConfig,
    getFeature,
    getFeatureType,
    isFeatureSupported,
    getProjectTypesForFeature,
    getDeviceTypesForFeature,
    getFileType,
  };
};

export const useVendorOptions = () => {
  const { t } = useTranslation();
  const {
    vendors,
    isLoading,
    getVendor,
    getProjectTypes,
    getDeviceTypes,
    getIde,
  } = useVendor();

  const getVendorOptions = () => {
    if (!vendors) return [];
    return vendors.map((vendor) => ({
      label: vendor.name,
      value: vendor.vendorId,
    }));
  };

  const getIdeOptionsForVendor = (vendorId: string) => {
    const ides = getVendor({ vendorId })?.ides;
    if (!ides) return [];

    return ides.map((ide) => {
      return {
        label: ide.name,
        value: ide.ideConfigId,
        additionalInformation: ide.additionalInformation,
      };
    });
  };

  const getProjectTypesOptions = (props: Partial<TGetIdeProps>) => {
    const projectTypes = getProjectTypes(props);

    return projectTypes.map((projectType) => {
      return {
        label: t(
          `projects:form.fields.project_type.${projectType.toLowerCase()}`,
        ),
        value: projectType,
      };
    });
  };

  const getDeviceTypesOptions = (vendorId: string, ideConfigId: string) => {
    const deviceTypes = getDeviceTypes({ vendorId, ideConfigId });

    return deviceTypes.map((deviceType) => {
      return {
        label: t(`device:device_types.${deviceType.toLowerCase()}`),
        value: deviceType,
      };
    });
  };

  const getProjectTypeFromFileExtensions = (
    vendorId: string,
    ideConfigId: string,
    fileExtension: string,
  ) => {
    const ide = getIde({ vendorId, ideConfigId });
    const fileTypes = ide?.fileTypes;

    if (!fileTypes) {
      return undefined;
    }

    return fileTypes.find(({ extension }) => extension === fileExtension)
      ?.projectType;
  };

  return {
    isLoading,
    getVendorOptions,
    getIdeOptionsForVendor,
    getProjectTypesOptions,
    getDeviceTypesOptions,
    getProjectTypeFromFileExtensions,
  };
};
